import type { ReactElement } from 'react';
import React, { useCallback } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import type { DateValidationError, DatePickerProps } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import type { DateTime } from 'luxon';
import { match } from 'ts-pattern';
import type { InputAdornmentProps } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import type {
  IInputBaseProps,
  RenderInputAdornmentType,
} from '../Input/InputControlWrapper';
import InputControlWrapper, {
  INPUT_HORIZONTAL_GAP_BETWEEN_ELEMENTS,
  INPUT_PADDING,
} from '../Input/InputControlWrapper';
import type { DateInterval } from './get-valid-date';
import { ELEMENT_ID_DATA_TEST_ATTRIBUTE } from '../../../constants/data-element-tracking-ids';

interface IDateCommonProps
  extends Omit<
  IInputBaseProps<DateTime | null>,
  'type' | 'placeholderText' | 'startAdornmentElement' | 'endAdornmentElement'
  > {
  dateInterval?: DateInterval;
}

type IDatePickerProps = IDateCommonProps &
DatePickerProps<DateTime> & {
  showOpenPicker?: boolean;
  onError?: (error: DateValidationError, value: DateTime | null) => void;
};

const OpenPickerIcon = () => <FontAwesomeIcon icon={faCalendar} />;

const DatePicker: React.FC<IDatePickerProps> = (inputProps) => {
  const {
    id,
    defaultValue,
    value,
    onChange,
    onError,
    label,
    customError,
    theme,
    dateInterval,
    showOpenPicker = true,
    dataElementId,
    ...props
  } = inputProps;

  const renderInputAdornment = useCallback(
    (
      params: InputAdornmentProps,
      renderEndAdornment: RenderInputAdornmentType,
    ) =>
      match(params.position)
        .with('end', () => (
          <>
            {renderEndAdornment(showOpenPicker ? params.children : undefined)}
          </>
        ))
        .otherwise(
          // eslint-disable-next-line max-len
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
          () => (params.children as ReactElement<any, any> | null) ?? null,
        ),
    [showOpenPicker],
  );

  return (
    <InputControlWrapper
      id={id}
      dataElementId={dataElementId}
      onChange={() => {}}
      value={undefined}
      defaultValue={undefined}
      label={label}
      customError={customError}
      theme={theme}
      renderInput={({ inputStyles, renderEndAdornment, id: controlId }) => (
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DesktopDatePicker
            defaultValue={defaultValue}
            value={value}
            onChange={onChange}
            onError={onError}
            minDate={dateInterval?.minAllowedDate}
            maxDate={dateInterval?.maxAllowedDate}
            formatDensity="spacious"
            slots={{
              inputAdornment: (params) =>
                renderInputAdornment(params, renderEndAdornment),
              openPickerIcon: OpenPickerIcon,
            }}
            slotProps={{
              textField: {
                sx: {
                  '& .MuiInputBase-root.MuiOutlinedInput-root': {
                    ...inputStyles(),
                    // It depends on the value,
                    // but the DataPicker doesn't have a value set in the InputControlWrapper,
                    // so we need to set this css property here
                    color: value
                      ? theme.colors.secondary
                      : theme.colors.grayscale.inactiveGrey1,
                    // Because the fieldset is always the last element of the DatePicker and
                    // we need some extra padding to have the correct padding for the last element.
                    paddingRight: `calc(${INPUT_PADDING} - ${INPUT_HORIZONTAL_GAP_BETWEEN_ELEMENTS})`,
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    border: 'unset',
                  },
                },
                id: controlId,
              },
              openPickerButton: {
                sx: { padding: 0, marginRight: 0 },
                disableRipple: true,
                id: `${dataElementId}-open-button`,
              },
            }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...{
              [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${dataElementId}-datepicker`,
            }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        </LocalizationProvider>
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
};

export default DatePicker;
